import React from 'react';
import TranslationNamespaces from '../../locales/TranslationNamespaces';
import { useTranslation } from 'react-i18next';
import { Divider, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { QIconButton, QInputForm, QPhoneInputForm, QWizard, QWizardStep, useQAlert } from '@kubiq-base/core';
import { Link } from 'react-router-dom';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { formatPhoneNumberIntl, isValidPhoneNumber } from 'react-phone-number-input';
import { useApi } from '../../state/ApiContext';
import { useCache } from '../../state/CachingContext';
import MessageBox from '../Shared/MessageBoxes/MessageBox';
import HeaderWithDescription from '../Shared/HeaderWithDescription';
import IdenticalPasswordForm from '../Shared/IdenticalPasswordForm';

type RegisterFields = {
  email?: string;
  fullName?: string;
  mobileNumber?: string;
  password?: string;
  passwordRep?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

const translationNamespace = TranslationNamespaces.register;

interface FormProps {
  formData: RegisterFields | undefined;
  onSubmit: (data: RegisterFields) => void;
  onError: (errors: Record<string, unknown>) => void;
}

const NameForm = ({ formData, onSubmit, onError }: FormProps): JSX.Element => {
  const { t } = useTranslation(translationNamespace);
  const { getCache } = useCache();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useFormContext();

  const email = getCache('email');

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
    >
      <QInputForm
        control={control}
        name='email'
        label={t('email')}
        defaultValue={email ? (email as string) : (formData?.email as string)}
        rules={{
          required: 'requiredField',
          pattern: {
            value:
              /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/,
            message: 'invalidEmail',
          },
        }}
        isError={errors.email && t(errors.email?.message)}
      />

      <QInputForm
        control={control}
        name='fullName'
        label={t('fullName')}
        defaultValue={formData?.fullName as string}
        rules={{ required: 'requiredField' }}
        isError={errors.fullName && t(errors.fullName.message)}
      />
    </form>
  );
};

const MiscForm = ({ formData, onSubmit, onError }: FormProps): JSX.Element => {
  const { t } = useTranslation(translationNamespace);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
    >
      <QPhoneInputForm
        control={control}
        name='mobileNumber'
        label={t('mobileNumber')}
        defaultValue={formData?.mobileNumber as string}
        rules={{ required: 'requiredField', validate: (value) => isValidPhoneNumber(value) }}
        isError={
          ((errors.mobileNumber && errors.mobileNumber.type === 'validate' && t('invalidNumber')) as string) ||
          (errors.mobileNumber && t(errors.mobileNumber.message))
        }
      />
    </form>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PasswordForm = ({ formData, onSubmit, onError }: FormProps): JSX.Element => {
  const { handleSubmit } = useFormContext();

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
    >
      <IdenticalPasswordForm
        translationNamespace={translationNamespace}
        passwordValue={formData?.password}
        passwordRepeatedValue={formData?.passwordRep}
      />
    </form>
  );
};

const Register = (): JSX.Element => {
  const { t } = useTranslation(translationNamespace);
  const theme = useTheme();
  const { postRequest } = useApi();
  const { getCache } = useCache();
  const session = getCache();
  const { enqueueAlert } = useQAlert();

  const [isCompleted, setIsCompleted] = React.useState<boolean>(false);
  const [formData, setFormData] = React.useState<RegisterFields | undefined>();
  const methods = useForm<RegisterFields>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: formData,
  });
  const { getValues, trigger, reset } = methods;

  const sm = !useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
  const lg = !useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

  const onSubmitStep = (data: RegisterFields): void =>
    setFormData((prevState) => {
      return { ...prevState, ...data };
    });

  const onSubmit = async (onComplete: (result: boolean) => void): Promise<void> => {
    const newState = { ...formData };

    delete newState.passwordRep;

    if (session && session.invitationguid) {
      newState.invitationGuid = session.invitationguid;
    }

    await postRequest('RegisterAccount', newState, undefined, ['validationError', 'accountCreationFailed'])
      .then(() => {
        onComplete(true);
        setIsCompleted(true);
      })
      .catch((error) => {
        onComplete(false);
        if (
          !error.response?.data.errors?.global?.includes('emailExists') &&
          !error.response?.data.errors?.global?.includes('alreadyVerified')
        ) {
          enqueueAlert(t('registerAccountError'), { type: 'error', autoHideDuration: 5000 });
        }
      });
  };

  // eslint-disable-next-line no-console
  const onError = (errors: Record<string, unknown>): void => console.error('Errors:', errors);

  React.useEffect(() => {
    reset(formData);
  }, [formData]);

  return (
    <>
      {!isCompleted ? (
        <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
          <div
            style={{
              position: 'absolute',
              top: theme.spacing(1),
              left: lg || sm ? 0 : '60%',
            }}
          >
            <QIconButton variant='text' to='/' component={Link} style={{ margin: `0 ${theme.spacing(1)}` }}>
              <ArrowBack />
            </QIconButton>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <HeaderWithDescription bigHeader translationNamespace={translationNamespace} />

            <FormProvider {...methods}>
              <QWizard
                noPaper
                beforeNext={async () => {
                  const validated = await trigger();
                  if (validated) onSubmitStep(getValues());
                  return validated;
                }}
                lastStepAction={() => {
                  return new Promise<boolean>((resolve) => onSubmit(resolve));
                }}
                minHeight={240}
              >
                <QWizardStep key='headerNameForm' title={t('headerNameForm')}>
                  <NameForm formData={formData} onSubmit={onSubmitStep} onError={onError} />
                </QWizardStep>

                <QWizardStep key='headerMiscForm' title={t('headerMiscForm')}>
                  <MiscForm formData={formData} onSubmit={onSubmitStep} onError={onError} />
                </QWizardStep>

                <QWizardStep key='headerIdenticalPasswordForm' title={t('headerIdenticalPasswordForm')}>
                  <PasswordForm formData={formData} onSubmit={onSubmitStep} onError={onError} />
                </QWizardStep>

                <QWizardStep key='headerConfirmForm' title={t('headerConfirmForm')}>
                  <div style={{ width: '100%' }}>
                    <Typography variant='h6'>{t('overview')}:</Typography>
                    {formData
                      ? Object.keys(formData).map(
                          (key) =>
                            key !== 'password' &&
                            key !== 'passwordRep' && (
                              <div key={key}>
                                <div style={{ display: 'flex', width: '100%', padding: `${theme.spacing(1)} 0` }}>
                                  <div style={{ width: '50%' }}>{t(key)}:</div>
                                  <div style={{ width: '50%' }}>
                                    {key === 'mobileNumber'
                                      ? `${formatPhoneNumberIntl(formData[key] as string)}`
                                      : `${formData[key]}`}
                                  </div>
                                </div>
                                <Divider />
                              </div>
                            ),
                        )
                      : null}
                  </div>
                </QWizardStep>
              </QWizard>
            </FormProvider>
          </div>
        </div>
      ) : (
        <>
          <HeaderWithDescription
            headerSuffix='CheckEmail'
            description='checkEmailDescription'
            translationNamespace={translationNamespace}
          />

          <MessageBox text={formData ? (formData.email as string) : 'No email'} emailBox />
        </>
      )}
    </>
  );
};

export default Register;
